import { engineComponent, NumberUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Reel } from "../GameComponents/Reel";
import { Symbol } from "../GameComponents/Symbol";
import { BetManager } from "../Managers/BetManager";
import { Symbols } from "../GameComponents/Symbols";
import { FrameTimerEvents } from "../Utils/FrameTimer";

/**
 * Reel2 handles:
 * - z-order
 * - usage of Normal instead of Rotation
 */
@engineComponent(ComponentTypes.DisplayObject)
export class Reel2 extends Reel {
    public static Tease: boolean = false;

    public valuesStrip: Array<number> = [];

    //******************************************************************************************************************
    //  SHOW SYMBOLS, SPIN ROLL
    //******************************************************************************************************************

    public showSymbols(useTransform: boolean = false): void {
        this.shouldBeStopping = false;
        this.isStopping = false;
        this.stoppingCounter = 0;
        this.isTeasing = false;
        this.teasingSymbols = [];

        this.x = this.positionX;
        this.y = this.positionY;

        if (!useTransform) {
            while (this.currentSymbols.length > 0) {
                this.returnSymbol(this.currentSymbols.pop());
            }
        }

        let bottomSymbols: Array<Symbol> = [];
        let topSymbols: Array<Symbol> = [];

        for (let i = 0; i < this.symbolBottomCount + this.symbolCount + this.symbolTopCount; i++) {
            let symbolId: number;
            let symbolCase: string;
            let symbolValue: number = 0;
            let useMultiplier: boolean = true;

            if (i < this.symbolBottomCount) {
                symbolId =
                    this.strip[
                        (this.data.index + this.symbolBottomCount + this.symbolCount - i - 1 + this.strip.length) %
                            this.strip.length
                    ];
                symbolValue =
                    this.valuesStrip[
                        (this.data.index + this.symbolBottomCount + this.symbolCount - i - 1 + this.strip.length) %
                            this.strip.length
                    ];
                symbolCase = i === this.symbolBottomCount - 1 ? Symbol.CaseRotation : Symbol.CaseBlur;
            } else if (i < this.symbolBottomCount + this.symbolCount) {
                symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - i - 1];
                if (this.data.customData != null && this.data.customData["scatterValues"] != null) {
                    symbolValue =
                        this.data.customData["scatterValues"][this.symbolBottomCount + this.symbolCount - i - 1];
                }
                if (this.data.customData != null && this.data.customData["scatterSymbolValues"] != null) {
                    symbolValue =
                        this.data.customData["scatterSymbolValues"][this.symbolBottomCount + this.symbolCount - i - 1];
                }
                if (this.data.customData != null && this.data.customData["extraScatterMultipliers"] != null) {
                    if (
                        this.data.customData["extraScatterMultipliers"][
                            this.symbolBottomCount + this.symbolCount - i - 1
                        ] > 0
                    ) {
                        symbolValue =
                            this.data.customData["extraScatterMultipliers"][
                                this.symbolBottomCount + this.symbolCount - i - 1
                            ];
                    }
                }
                symbolCase = Symbol.CaseRotation;
                useMultiplier = false;
            } else {
                symbolId =
                    this.strip[
                        (this.data.index + this.symbolBottomCount + this.symbolCount - i - 1 + this.strip.length) %
                            this.strip.length
                    ];
                symbolValue =
                    this.valuesStrip[
                        (this.data.index + this.symbolBottomCount + this.symbolCount - i - 1 + this.strip.length) %
                            this.strip.length
                    ];
                symbolCase = i === this.symbolBottomCount + this.symbolCount ? Symbol.CaseRotation : Symbol.CaseBlur;
            }

            if (!useTransform) {
                // when we don't want use symbols from WinAnimator
                this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase, symbolValue, useMultiplier));
            } else {
                // currentSymbols are used from WinAnimator. We only need to add new top and bottom symbols
                if (i < this.symbolBottomCount) {
                    bottomSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase, symbolValue, useMultiplier));
                } else if (i > this.symbolBottomCount + this.symbolCount - 1) {
                    topSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase, symbolValue, useMultiplier));
                } else if (this.currentSymbols[this.symbolCount - i] == null) {
                    //add symbol below expanded wild
                    this.currentSymbols[this.symbolCount - i] = this.getSymbol(
                        symbolId - 1,
                        symbolCase,
                        symbolValue,
                        useMultiplier,
                    );
                }
            }
        }

        if (useTransform) {
            while (bottomSymbols.length > 0) {
                this.currentSymbols.push(bottomSymbols.shift());
            }

            while (topSymbols.length > 0) {
                this.currentSymbols.unshift(topSymbols.shift());
            }
        }

        for (let i = 0; i < this.currentSymbols.length; i++) {
            this.addChild(this.currentSymbols[i]);

            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;
        }

        if (this.data.transform) {
            for (let i = this.symbolTopCount; i < this.symbolCount + this.symbolTopCount; i++) {
                this.currentSymbols[i].goToCase(Symbol.CaseEmpty);
            }
        }

        this.moveToTop();
    }

    protected updateReel(): void {
        this.returnSymbol(this.currentSymbols.pop());

        if (this.shouldBeStopping && !this.isStopping) {
            if (this.previousReel === null) {
                this.isStopping = true;
            } else if (this.previousReel.isStopping) {
                this.isStopping = true;
            }
        }

        this.stripIndex = (this.stripIndex - 1 + this.strip.length) % this.strip.length;

        let symbolId = this.strip[this.stripIndex];
        let symbolCase = Symbol.CaseBlur;
        let symbolValue: number = this.valuesStrip[this.stripIndex];
        let useMultiplier: boolean = true;

        if (this.isStopping) {
            if (this.stoppingCounter < this.symbolBottomCount) {
                symbolId =
                    this.strip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            this.strip.length) %
                            this.strip.length
                    ];
                symbolValue =
                    this.valuesStrip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            this.strip.length) %
                            this.strip.length
                    ];
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount - 1 ? Symbol.CaseRotation : Symbol.CaseBlur;
            } else if (this.stoppingCounter < this.symbolBottomCount + this.symbolCount) {
                symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1];
                if (this.data.customData != null && this.data.customData["scatterValues"] != null) {
                    symbolValue =
                        this.data.customData["scatterValues"][
                            this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1
                        ];
                }
                if (this.data.customData != null && this.data.customData["scatterSymbolValues"] != null) {
                    symbolValue =
                        this.data.customData["scatterSymbolValues"][
                            this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1
                        ];
                }
                if (this.data.customData != null && this.data.customData["extraScatterMultipliers"] != null) {
                    if (
                        this.data.customData["extraScatterMultipliers"][
                            this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1
                        ] > 0
                    ) {
                        symbolValue =
                            this.data.customData["extraScatterMultipliers"][
                                this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1
                            ];
                    }
                }
                symbolCase = Symbol.CaseRotation;
                useMultiplier = false;
            } else {
                symbolId =
                    this.strip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            this.strip.length) %
                            this.strip.length
                    ];
                symbolValue =
                    this.valuesStrip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            this.strip.length) %
                            this.strip.length
                    ];
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount + this.symbolCount
                        ? Symbol.CaseRotation
                        : Symbol.CaseBlur;
            }

            this.stoppingCounter++;
        }

        this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase, symbolValue, useMultiplier));

        this.addChild(this.currentSymbols[0]);

        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;
        }

        if (
            this.isStopping &&
            this.stoppingCounter === this.symbolBottomCount + this.symbolCount + this.symbolTopCount
        ) {
            this.spinPostRoll();
        } else {
            this.spinRoll();
        }

        this.moveToTop();
    }

    private moveToTop(): void {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];

            if (symbol !== null && symbol.id === 9) {
                symbol.parent.addChild(symbol);
            }
        }

        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];

            if (symbol !== null && symbol.id === 10) {
                symbol.parent.addChild(symbol);
            }
        }
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    protected getSymbol(
        symbolId: number,
        symbolCase: string,
        amount: number = 0,
        useMultiplier: boolean = true,
    ): Symbol {
        if (symbolCase === Symbol.CaseRotation) {
            symbolCase = Symbol.CaseNormal;
        }

        let multiplier: boolean = useMultiplier;
        if (symbolId === 9) {
            multiplier = false;
        }

        const symbol = super.getSymbol(symbolId, symbolCase);
        if (amount > 0) {
            if (multiplier) {
                symbol.setAmount(parseFloat(NumberUtils.multiply(amount, BetManager.Bet)));
            } else {
                symbol.setAmount(amount);
            }
        }

        if (Reel2.Tease) {
            if (symbol.id !== 8) {
                symbol.alpha = 0.7;
            }
            if (symbol.id === 7) {
                symbol.alpha = 0.5;
            }
        }

        return symbol;
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/Reel2.ts
 **/